import { useEffect, useRef, useState } from 'react';
import ClusterDropdown from '../components/ClusterDropdown';
import StatusTable from '../components/StatusTable';
import IncidentList from '../components/IncidentList';
import Legend from '../components/Legend';
import LoadingSpinner from '../components/LoadingSpinner';
import PlvStatus from '../components/PlvStatus';
import LoadBalancerStatus from '../components/LoadBalancerStatus';
import EventForm from '../components/EventForm';
import MaintenanceForm from '../components/MaintenanceForm';
import MaintenanceCard from '../components/MaintenanceCard';
import SetStatusForm from '../components/SetStatusForm';
import HistoryPage from '../components/HistoryPage';
import { syncCluster, getPlvStatus, getClusters, addCluster, removeCluster, addCustomIncident, updateIncident, publishIncident, unpublishIncident, erasePastIncident, addMaintenance, getActiveMaintenance, getArchivedMaintenance, updateMaintenance, archiveMaintenance, eraseArchivedMaintenance, setClusterStatus, getActiveIncidents, getArchivedIncidents } from '../services/api';
import { sortIncidents, sortMaintenances } from '../services/sorting';

const SHOW_PLV = false;

function App() {
  const [clusters, setClusters] = useState([]);
  const [selected, setSelected] = useState(null);
  const [clusterStatus, setClusterStatusState] = useState({ workloads: [], lastUpdated: null, clusterName: '', overall_status: 'operational', region: '', customer_alias: '' });
  const [plvData, setPlvData] = useState({ plvStatus: null, plvUrl: null });
  const [lbStatus, setLbStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [publishingIncidentId, setPublishingIncidentId] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [editingMaintenance, setEditingMaintenance] = useState(null);
  const [activeMaintenance, setActiveMaintenance] = useState(null);
  const [archivedMaintenance, setArchivedMaintenance] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [showSetStatusForm, setShowSetStatusForm] = useState(false);
  const [activeIncidents, setActiveIncidents] = useState([]);
  const [archivedIncidents, setArchivedIncidents] = useState([]);
  const [page, setPage] = useState(window.location.pathname === '/history' ? 'history' : 'main');
  const skipNextSyncRef = useRef(false);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setPage(path === '/history' ? 'history' : 'main');
  };

  useEffect(() => {
    const handlePopState = () => {
      setPage(window.location.pathname === '/history' ? 'history' : 'main');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const fetchIncidents = () => {
    getActiveIncidents().then(res => {
      setActiveIncidents(sortIncidents(res.data || []));
    });
    getArchivedIncidents().then(res => {
      setArchivedIncidents(sortIncidents(res.data || []));
    });
  };

  const handlePublishEvent = (incidentData) => {
    if (!selected) {
      console.error("No cluster selected");
      return;
    }
    setPublishing(true);

    let incidentApiCall;
    if (editingIncident) {
      const isPublished = activeIncidents.some(p => p.id === editingIncident.id);
      if (isPublished) {
        incidentApiCall = updateIncident(incidentData);
      } else {
        incidentApiCall = publishIncident(incidentData);
      }
    } else {
      incidentApiCall = addCustomIncident(incidentData);
    }

    incidentApiCall
      .then(() => {
        setShowEventForm(false);
        setEditingIncident(null);
        fetchIncidents();
      })
      .catch(err => console.error("Failed to publish event:", err))
      .finally(() => setPublishing(false));
  };

  const handlePublishMaintenance = (maintenanceData) => {
    if (!selected) {
      console.error("No cluster selected");
      return;
    }
    setPublishing(true);
    addMaintenance({
      ...maintenanceData,
      start_time: new Date(maintenanceData.start_time),
      end_time: maintenanceData.end_time ? new Date(maintenanceData.end_time) : null,
    })
      .then(() => {
        setShowMaintenanceForm(false);
        setEditingMaintenance(null);
        fetchMaintenanceData();
      })
      .catch(err => console.error("Failed to publish maintenance:", err))
      .finally(() => setPublishing(false));
  };

  const handleUpdateMaintenance = (maintenanceData) => {
    if (!selected) {
      console.error("No cluster selected");
      return;
    }
    setPublishing(true);
    updateMaintenance({
      ...maintenanceData,
      start_time: new Date(maintenanceData.start_time),
      end_time: maintenanceData.end_time ? new Date(maintenanceData.end_time) : null,
    })
      .then(() => {
        setShowMaintenanceForm(false);
        setEditingMaintenance(null);
        fetchMaintenanceData();
      })
      .catch(err => console.error("Failed to update maintenance:", err))
      .finally(() => setPublishing(false));
  };

  const handleArchiveMaintenance = (maintenance) => {
    if (!selected) {
      console.error("No cluster selected");
      return;
    }
    archiveMaintenance(maintenance.id)
      .then(() => {
        setActiveMaintenance(null);
        setArchivedMaintenance(prev => sortMaintenances([...prev, maintenance]));
      })
      .catch(err => console.error("Failed to archive maintenance:", err));
  };

  const handlePublishIncident = (incident) => {
    setPublishingIncidentId(incident.id);
    publishIncident(incident)
      .then(() => {
        fetchIncidents();
        fetchData();
      })
      .catch(err => {
        console.error("Failed to publish incident:", err);
      })
      .finally(() => setPublishingIncidentId(null));
  };

  const handleUnpublishIncident = (incident) => {
    setPublishingIncidentId(incident.id);
    unpublishIncident(incident)
      .then(() => {
        fetchIncidents();
        fetchData();
      })
      .catch(err => {
        console.error("Failed to unpublish incident:", err);
      })
      .finally(() => setPublishingIncidentId(null));
  };



  const handleSetStatus = (statusData) => {
    if (!selected) return;
    setClusterStatus(selected.name, statusData)
      .then(() => {
        setShowSetStatusForm(false);
        setClusterStatusState(prev => ({
          ...prev,
          overall_status: statusData.status,
        }));
        fetchData();
      })
      .catch(err => console.error("Failed to set cluster status:", err));
  };

  const handleAddCluster = (clusterName) => {
    setSyncError(null);
    setLoading(true);

    addCluster(clusterName)
      .then(() => {
        return Promise.all([syncCluster(clusterName), getClusters()])
          .then(([syncRes, clustersRes]) => {
            const clusterData = clustersRes.data || [];
            setClusters(clusterData);
            const newCluster = clusterData.find(c => c.name === clusterName);

            const data = syncRes.data;
            setClusterStatusState({
              workloads: data.workloads || [],
              incidents: sortIncidents(data.gcp_incidents || []),
              lastUpdated: data.lastUpdated,
              clusterName: data.clusterName,
              overall_status: data.overall_status || 'operational',
              region: data.region || '',
              customer_alias: data.customer_alias || '',
            });
            setLbStatus(data.lb_error_status || null);
            fetchMaintenanceData();
            fetchIncidents();

            if (newCluster) {
              skipNextSyncRef.current = true;
              setSelected(newCluster);
              localStorage.setItem('selectedCluster', newCluster.name);
            }
            setLoading(false);
          })
          .catch(err => {
            const msg = err.response?.data?.error || err.message || 'Unknown error';
            setSyncError(`Cluster "${clusterName}" was added but sync failed: ${msg}`);
            getClusters().then(res => setClusters(res.data || []));
            setLoading(false);
          });
      })
      .catch((err) => {
        const sa = err.response?.data?.service_account || 'health-api-access@<project>.iam.gserviceaccount.com';
        const project = err.response?.data?.project || clusterName;
        setSyncError(
          `Cluster "${clusterName}" could not be added due to permission issues. To resolve this, add the following roles to project "${project}":\n` +
          `gcloud projects add-iam-policy-binding ${project} --member="serviceAccount:${sa}" --role="roles/container.viewer"\n` +
          `gcloud projects add-iam-policy-binding ${project} --member="serviceAccount:${sa}" --role="roles/monitoring.viewer"\n` +
          `gcloud projects add-iam-policy-binding ${project} --member="serviceAccount:${sa}" --role="roles/logging.viewer"`
        );
        setLoading(false);
      });
  };

  const handleRemoveCluster = (clusterName) => {
    removeCluster(clusterName)
      .then(() => {
        return getClusters();
      })
      .then(res => {
        const clusterData = res.data || [];
        setClusters(clusterData);
        if (selected && selected.name === clusterName) {
          if (clusterData.length > 0) {
            setSelected(clusterData[0]);
            localStorage.setItem('selectedCluster', clusterData[0].name);
          } else {
            setSelected(null);
          }
        }
      })
      .catch(err => console.error("Failed to remove cluster:", err));
  };

 const fetchMaintenanceData = () => {
   getActiveMaintenance().then(res => setActiveMaintenance(res.data?.id ? res.data : null));
   getArchivedMaintenance().then(res => {
    setArchivedMaintenance(sortMaintenances(res.data));
   });
 }

  useEffect(() => {
    window.history.pushState({}, '', '/');
    getClusters().then(res => {
      const clusterData = res.data || [];
      setClusters(clusterData);
      fetchIncidents();
      fetchMaintenanceData();
      if (clusterData.length > 0) {
        const savedClusterName = localStorage.getItem('selectedCluster');
        const savedCluster = clusterData.find(c => c.name === savedClusterName);
        setSelected(savedCluster || clusterData[0]);
      } else {
        setLoading(false);
      }
    });
  }, []);

  const fetchData = () => {
    if (!selected) return;

    const fetchClusterData = () => {
      setLoading(true);
      setSyncError(null);
      Promise.all([
        syncCluster(selected.name).catch(err => {
          const empty = { data: { workloads: [], gcp_incidents: [], lastUpdated: null, clusterName: selected.name, overall_status: 'not-available', region: '', lb_error_status: null } };
          if (err.response && err.response.status === 404) {
            return empty;
          }
          const msg = err.response?.data?.error || err.message || 'Unknown error';
          setSyncError(`Sync failed: ${msg}. Check that the service account has the required IAM permissions on this project.`);
          return empty;
        }),
        SHOW_PLV ? getPlvStatus(selected.name).catch(err => {
          if (err.response && err.response.status === 404) {
            return { data: { plv_status: "not_applicable", plv_url: "" } };
          }
          throw err;
        }) : Promise.resolve({ data: { plv_status: "not_applicable", plv_url: "" } }),
      ]).then(([syncRes, plvStatusRes]) => {
        const data = syncRes.data;
        const gcpList = sortIncidents(data.gcp_incidents || []);

        setClusterStatusState({
          workloads: data.workloads || [],
          incidents: gcpList,
          lastUpdated: data.lastUpdated,
          clusterName: data.clusterName,
          overall_status: data.overall_status || 'operational',
          region: data.region || '',
          customer_alias: data.customer_alias || '',
          service_statuses: data.service_statuses || {},
        });
        setLbStatus(data.lb_error_status || null);
        setPlvData({
          plvStatus: plvStatusRes.data.plv_status,
          plvUrl: plvStatusRes.data.plv_url,
        });
      }).finally(() => {
        setLoading(false);
      });
    };

    fetchClusterData();
    fetchMaintenanceData();
  };

  useEffect(() => {
    if (selected) {
      if (skipNextSyncRef.current) {
        skipNextSyncRef.current = false;
        return;
      }
      fetchData();
    }
  }, [selected]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (page === 'history') {
    return (
      <HistoryPage
        pastIncidents={archivedIncidents}
        archivedMaintenance={archivedMaintenance}
        onEraseIncident={(incident) => {
          erasePastIncident(incident)
            .then(() => fetchIncidents())
            .catch(err => console.error("Failed to erase incident:", err));
        }}
        onEraseMaintenance={(maintenance) => {
          eraseArchivedMaintenance(maintenance.id)
            .then(() => fetchMaintenanceData())
            .catch(err => console.error("Failed to erase maintenance:", err));
        }}
        onBack={() => navigate('/')}
        backLabel="Back to Dashboard"
      />
    );
  }

  const allIncidents = sortIncidents(
    [...activeIncidents, ...(clusterStatus.incidents || [])].filter(
      (v, i, a) => a.findIndex(t => t.id === v.id) === i
    )
  );

  return (
    <div className="min-h-screen bg-white p-8">
      <header className="py-4 text-center">
        <img src="/agni-blue.png" alt="AGNI Status Page" className="w-64 mx-auto" />
      </header>

      {syncError && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-300 rounded text-yellow-900 text-sm">
          {syncError.split('\n').map((line, i) =>
            i === 0
              ? <p key={i} className="mb-2">{line}</p>
              : <pre key={i} className="text-xs bg-yellow-100 rounded px-2 py-1 mt-1 overflow-x-auto">{line.trim()}</pre>
          )}
        </div>
      )}

      <div className="mb-6">
        <div className="flex justify-between items-center">
        	<div className="flex items-center space-x-2">
        	  <ClusterDropdown clusters={clusters} onSelect={(cluster) => {
        	    setSelected(cluster);
        	    localStorage.setItem('selectedCluster', cluster.name);
        	  }} selected={selected} onAddCluster={handleAddCluster} onRemoveCluster={handleRemoveCluster} />
        	  <button
        	    onClick={() => {
        	      setSyncing(true);
        	      fetchData();
        	      fetchIncidents();
        	      setSyncing(false);
        	    }}
        	    disabled={syncing || !selected}
        	    className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 disabled:bg-gray-400"
        	  >
        	    {syncing ? 'Syncing...' : 'Force Sync'}
        	  </button>
        	</div>
        	 <div className="flex items-center space-x-4">
        	   <div className="flex items-center space-x-2">
        	     <span className="text-sm font-medium text-gray-600">Current Status:</span>
        	     <span className={`px-2.5 py-1 text-sm font-bold rounded-none border-2 ${
        	       clusterStatus.overall_status === 'operational' ? 'border-green-600 text-green-600' :
        	       clusterStatus.overall_status === 'degraded' ? 'border-yellow-600 text-yellow-600' :
        	       clusterStatus.overall_status === 'outage' ? 'border-red-800 text-red-800' :
        	       'border-gray-500 text-gray-500'
        	     }`}>
        	       {clusterStatus.overall_status}
        	     </span>
        	      </div>
        	      <div className="flex items-center space-x-2">
        	     <span className="text-sm font-medium text-gray-600">Region:</span>
        	     <span className="text-sm text-gray-800">{clusterStatus.region}</span>
        	      </div>
        	      <button
        	     onClick={() => {
        	       setEditingIncident(null);
        	       setShowEventForm(true);
        	     }}
        	     disabled={publishing}
        	     className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
        	      >
        	     {publishing ? 'Publishing...' : 'Publish Event'}
        	      </button>
        	        <button
        	          onClick={() => {
        	            if (activeMaintenance) {
        	              alert("An active notice already exists. Please archive it before creating a new one.");
        	            } else {
        	              setEditingMaintenance(null);
        	              setShowMaintenanceForm(true);
        	            }
        	          }}
        	          disabled={publishing || !!activeMaintenance}
        	          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400"
        	      >
        	          {publishing ? 'Publishing...' : 'Add Notice'}
        	      </button>
        	      <button
        	        onClick={() => setShowSetStatusForm(true)}
        	        disabled={!selected}
        	        className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-400"
        	      >
        	        Set Status
        	      </button>
        	    </div>
        </div>
        {selected && (
          <p className="mt-2 text-gray-700">
            Customer Alias: <span className="font-bold">{clusterStatus.customer_alias || selected.name}</span>
          </p>
        )}
      </div>

      {showEventForm && (
        <EventForm
        	 cluster={selected.name}
        	 onPublish={handlePublishEvent}
        	 onCancel={() => {
        	   setShowEventForm(false);
        	   setEditingIncident(null);
        	 }}
        	 incident={editingIncident}
        />
      )}

     {showMaintenanceForm && (
       <MaintenanceForm
         onPublish={editingMaintenance ? handleUpdateMaintenance : handlePublishMaintenance}
         onCancel={() => {
           setShowMaintenanceForm(false);
           setEditingMaintenance(null);
         }}
         maintenance={editingMaintenance}
       />
     )}

     {showSetStatusForm && selected && (
       <SetStatusForm
         clusterName={selected.name}
         currentStatus={clusterStatus.overall_status}
         currentCustomerAlias={clusterStatus.customer_alias}
         currentServiceStatuses={clusterStatus.service_statuses}
         onSubmit={handleSetStatus}
         onCancel={() => setShowSetStatusForm(false)}
       />
     )}

      <div>
        <Legend />
      </div>

      <div className="overflow-x-auto">
        <StatusTable
          workloads={clusterStatus.workloads}
          lastUpdated={clusterStatus.lastUpdated}
          clusterName={clusterStatus.clusterName}
        />
      </div>

      {selected && (
        <div className="mt-2 mb-4">
          <a
            href={`https://grafana.agnieng.net/d/x-7Zfaf4z/all-cluster-health?orgId=1&from=now-5m&to=now&timezone=browser&var-project=${selected.name}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline text-sm font-medium"
          >
            View Grafana Dashboard
          </a>
        </div>
      )}

      <div className="space-y-4">
        {SHOW_PLV && <PlvStatus status={plvData.plvStatus} url={plvData.plvUrl} />}
        <LoadBalancerStatus status={lbStatus} clusterName={selected ? selected.name : ''} />
      </div>

      <div className="mt-8">
       <h2 className="text-2xl font-bold mb-2">Active Notice</h2>
       <hr className="border-t border-gray-200 mb-4" />
       {activeMaintenance ? (
        <MaintenanceCard
            key={activeMaintenance.id}
            maintenance={activeMaintenance}
            onEdit={(maintenance) => {
                setEditingMaintenance(maintenance);
                setShowMaintenanceForm(true);
            }}
            onArchive={handleArchiveMaintenance}
        />
    ) : (
        <p className="text-gray-500 italic">No active notice.</p>
    )}
      </div>

      <div className="mt-8">
       <h2 className="text-2xl font-bold mb-2">Incidents</h2>
       <hr className="border-t border-gray-200 mb-4" />
        <IncidentList
          incidents={allIncidents}
          publishedIncidents={activeIncidents}
          onPublish={handlePublishIncident}
          onUnpublish={handleUnpublishIncident}
          onEdit={(incident) => {
            setEditingIncident(incident);
            setShowEventForm(true);
          }}
          onIncidentClick={(incident) => {
            setEditingIncident(incident);
            setShowEventForm(true);
          }}
          publishingIncidentId={publishingIncidentId}
        />
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/history')}
            className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
          >
            View Incident &amp; Notice History →
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
